import React from 'react';
import type { ISODateRange } from 'shared/src/date-string-utils/types';
import { useCubeQuery } from '@/components/display/use-cube-query';
import { useLineItemContext } from '@/features/line-items/line-items-context';
import { ActiveOverUnderMetric, ExpiredOverUnderMetric } from './over-under-metrics';
import { getDateRangeState } from './metric-date-utils';
import { MetricSkeleton } from '@/components/feedback/metric-skeleton';

type MarginOverUnderMetricProps = {
  dateRange: ISODateRange;
};

export function MarginOverUnderMetric({ dateRange }: MarginOverUnderMetricProps) {
  const { lineItem } = useLineItemContext();

  const result = useCubeQuery({
    dimensions: ['line_item_metrics_daily.planned_margin'],
    measures: ['line_item_metrics_daily.margin_actual', 'line_item_metrics_daily.margin_variance'],
    timeDimensions: [
      {
        dimension: 'line_item_metrics_daily.day',
        dateRange: [dateRange.from, dateRange.to]
      }
    ],
    filters: [
      {
        member: 'line_item_metrics_daily.line_item_id',
        operator: 'equals',
        values: [lineItem.id]
      }
    ]
  });

  const isExpired = getDateRangeState(dateRange) === 'expired';

  if (result.status === 'loading') return <MetricSkeleton />;
  if (result.status === 'error') return null;
  if (result.formattedRows.length === 0) {
    if (isExpired) {
      return (
        <ExpiredOverUnderMetric
          title="Margin"
          actualValue="-.--"
          plannedValue="-.--"
          variance={0}
          varianceFormatted=""
          favorableDirection="over"
        />
      );
    }
    return (
      <ActiveOverUnderMetric
        title="Margin"
        actualValue="-.--"
        plannedValue="-.--"
        variance={0}
        varianceFormatted=""
        favorableDirection="over"
      />
    );
  }

  const {
    'line_item_metrics_daily.margin_actual': marginActual,
    'line_item_metrics_daily.planned_margin': plannedMargin,
    'line_item_metrics_daily.margin_variance': varianceFormatted
  } = result.formattedRows[0];
  const { 'line_item_metrics_daily.margin_variance': variance } = result.rows[0];

  if (isExpired) {
    return (
      <ExpiredOverUnderMetric
        title="Margin"
        actualValue={marginActual}
        plannedValue={plannedMargin}
        variance={variance}
        varianceFormatted={varianceFormatted}
        favorableDirection="over"
      />
    );
  }

  return (
    <ActiveOverUnderMetric
      title="Margin"
      actualValue={marginActual}
      plannedValue={plannedMargin}
      variance={variance}
      varianceFormatted={varianceFormatted}
      favorableDirection="over"
    />
  );
}
